import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * An agent's inline visualization (html_render) in the chat thread — the web
 * twin of the desktop's HtmlRenderFrame (analystbro-desktop/src/
 * HtmlRenderFrame.tsx + htmlRender.ts), same height and theme rules.
 *
 * The desktop host uploads the HTML to the relay, which serves it with a CSP
 * sandbox; this iframe is sandboxed too and NEVER gets allow-same-origin, so
 * the visual cannot reach the site, its storage or the user's login.
 */

const MIN_H = 80;
const MAX_H = 2000;
const COLUMN_WIDTH = 832;
const clamp = (h) => Math.min(MAX_H, Math.max(MIN_H, Math.round(h)));

// The taller of the heights measured at the nearest widths on each side.
function measuredHeight(heights, width) {
  const above = heights.findIndex(([w]) => w >= width);
  const high = above === -1 ? heights.length - 1 : above;
  const low = heights[high][0] === width ? high : Math.max(0, high - 1);
  return Math.max(heights[low][1], heights[high][1]);
}

function frameHeight(ref, width, contentHeight) {
  const heights = Array.isArray(ref.heights) && ref.heights.length ? [...ref.heights].sort((a, b) => a[0] - b[0]) : null;
  const height = typeof ref.height === 'number' ? ref.height : 400;
  if (!heights) return clamp(Math.min(height, contentHeight ?? height));
  const cap = measuredHeight(heights, COLUMN_WIDTH) > height ? height : MAX_H;
  return clamp(Math.min(cap, contentHeight ?? measuredHeight(heights, width)));
}

// Same variables the desktop injects; --background matches this thread.
const THEME = {
  appearance: 'dark',
  variables: {
    '--background': '#1a1c2a', '--foreground': '#e6e9ef',
    '--muted': '#161a21', '--muted-foreground': '#8b94a5',
    '--card': '#161a21', '--card-foreground': '#e6e9ef',
    '--popover': '#1b2230', '--popover-foreground': '#e6e9ef',
    '--secondary': '#1b2230', '--secondary-foreground': '#e6e9ef',
    '--border': '#252b36', '--input': '#252b36', '--ring': '#4f9cf9',
    '--primary': '#4f9cf9', '--primary-foreground': '#0f1115',
    '--accent': '#4f9cf9', '--accent-foreground': '#0f1115',
    '--accent-surface': '#16243a', '--accent-surface-foreground': '#9cc5fb',
    '--destructive': '#f85149', '--destructive-foreground': '#ffa198', '--destructive-surface': '#3a1716',
    '--warning': '#d29922', '--warning-foreground': '#e3b341', '--warning-surface': '#3a2a10',
    '--success': '#3fb950', '--success-foreground': '#56d364',
    '--info': '#3b82f6', '--info-foreground': '#60a5fa',
    '--code-background': '#10141b', '--code-foreground': '#e6e9ef',
    '--chart-1': '#4f9cf9', '--chart-2': '#2dd4bf', '--chart-3': '#fbbf24',
    '--chart-4': '#c084fc', '--chart-5': '#fb7185', '--chart-6': '#a3e635',
    '--radius': '0.625rem',
    '--font-sans': '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    '--font-mono': '"SF Mono", "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace',
  },
};
const THEME_FRAGMENT = `#t3-theme=${encodeURIComponent(JSON.stringify(THEME))}`;

// MCP Apps protocol messages from the frame (JSON-RPC over postMessage).
function readContentHeight(d) {
  if (!d || d.jsonrpc !== '2.0' || d.method !== 'ui/notifications/size-changed') return undefined;
  const h = d.params?.height;
  return typeof h === 'number' && Number.isFinite(h) && h > 0 ? h : undefined;
}
function readLinkRequest(d) {
  if (!d || d.jsonrpc !== '2.0' || d.method !== 'ui/open-link') return undefined;
  const url = d.params?.url;
  return (typeof d.id === 'string' || typeof d.id === 'number') && typeof url === 'string' && /^https?:\/\//i.test(url)
    ? { id: d.id, url } : undefined;
}

export default function HtmlRenderFrame({ visual }) {
  const boxRef = useRef(null);
  const frameRef = useRef(null);
  const [width, setWidth] = useState(COLUMN_WIDTH);
  const [contentHeight, setContentHeight] = useState();
  const height = frameHeight(visual, width, contentHeight);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    setWidth(box.clientWidth);
    const ro = new ResizeObserver(([e]) => e && setWidth(e.contentRect.width));
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  // Listen from the commit that inserts the frame; a fast page's first height
  // report could otherwise be missed.
  useLayoutEffect(() => {
    const on = (e) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      const h = readContentHeight(e.data);
      if (h !== undefined) { setContentHeight(h); return; }
      const link = readLinkRequest(e.data);
      if (link) {
        window.open(link.url, '_blank', 'noopener,noreferrer');
        frameRef.current?.contentWindow?.postMessage({ jsonrpc: '2.0', id: link.id, result: {} }, '*');
      }
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, []);

  if (!visual.url) {
    // The host could not upload it (an older desktop build, or a failure).
    return (
      <div className="mt-2 rounded-lg border border-gray-700/60 px-3 py-2 text-xs text-gray-400">
        {visual.title || 'Visualization'} — open this chat in AnalystBro Desktop to see it.
      </div>
    );
  }
  return (
    <div ref={boxRef} className="mt-2 w-full" style={{ height }}>
      <iframe
        ref={frameRef}
        src={visual.url + THEME_FRAGMENT}
        title={visual.title || 'Visualization'}
        sandbox="allow-scripts allow-forms"
        referrerPolicy="no-referrer"
        className="w-full h-full border-0 block"
      />
    </div>
  );
}
